'use client'

import { useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { FilterSidebarRefinementEnabled } from '@/components/assets-explorer/filters/filter-sidebar'
import { useFiltersStore } from '@/lib/store/filters-store'

export default function useFilterSummary(refinementEnabled: FilterSidebarRefinementEnabled = true) {
  const filters = useFiltersStore(useShallow((state) => state.filters))
  const search = useFiltersStore((state) => state.refinementQuery)
  const appliedKeywords = useFiltersStore(useShallow((state) => state.appliedKeywords))

  const appliedFilterNames = useMemo(() => {
    return filters
      .filter((filter) => filter.enabled)
      .filter((filter) => filter.value !== undefined)
      .map((filter) => filter.name)
  }, [filters])

  const isSearchRefinementEnabled = useMemo(() => {
    if (typeof refinementEnabled === 'boolean') {
      return refinementEnabled
    }
    return refinementEnabled.search !== false
  }, [refinementEnabled])

  const isTagsRefinementEnabled = useMemo(() => {
    if (typeof refinementEnabled === 'boolean') {
      return refinementEnabled
    }
    return refinementEnabled.tags !== false
  }, [refinementEnabled])

  const isSomeRefinementEnabled = useMemo(() => {
    if (typeof refinementEnabled === 'boolean') {
      return refinementEnabled
    }
    return isSearchRefinementEnabled || isTagsRefinementEnabled
  }, [refinementEnabled, isSearchRefinementEnabled, isTagsRefinementEnabled])

  const hasFiltersApplied = useMemo(() => {
    return appliedFilterNames.length > 0 || appliedKeywords.length > 0 || (isSearchRefinementEnabled && !!search)
  }, [appliedFilterNames.length, appliedKeywords.length, isSearchRefinementEnabled, search])

  const appliedFiltersCount = useMemo(() => {
    return appliedFilterNames.length + appliedKeywords.length + (isSearchRefinementEnabled && !!search ? 1 : 0)
  }, [appliedFilterNames.length, appliedKeywords.length, isSearchRefinementEnabled, search])

  return {
    appliedFilterNames,
    appliedFiltersCount,
    hasFiltersApplied,
    isSearchRefinementEnabled,
    isSomeRefinementEnabled,
    isTagsRefinementEnabled,
  }
}
